import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaLinkedin, FaTwitter, FaInstagram, FaTimes } from 'react-icons/fa';
import './Navbar.css';
import SignOut from './signout';
import { auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';

const firestore = getFirestore();

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [userAccounts, setUserAccounts] = useState([]);
  const [hasMultipleAccounts, setHasMultipleAccounts] = useState(false);
  const navigate = useNavigate();

  // Function to find user in all collections
  const findUserInAllCollections = async (userEmail, userId) => {
    const foundAccounts = [];

    try {
      // Check 1: Investment/Trading users
      const investmentQuery = query(
        collection(firestore, "UserRegistrations"), 
        where("email", "==", userEmail)
      );
      const investmentSnapshot = await getDocs(investmentQuery);
      
      if (!investmentSnapshot.empty) {
        const userData = investmentSnapshot.docs[0].data();
        foundAccounts.push({
          type: 'investment',
          data: userData,
          id: investmentSnapshot.docs[0].id,
          title: 'Funds Management',
          route: userData.subscriptionPlan === 'trial' ? '/investdashboard' : 
                 (userData.status === 'active' || userData.backendRegistered) ? 
                 '/investdashboard' : '/USDTPayment'
        });
      }

      // Check 2: Training program users
      const trainingQuery = query(
        collection(firestore, "FormRegistration"), 
        where("emailAddress", "==", userEmail)
      );
      const trainingSnapshot = await getDocs(trainingQuery);
      
        if (!trainingSnapshot.empty) {
        const userData = trainingSnapshot.docs[0].data();
        foundAccounts.push({
          type: 'training',
          data: userData,
          id: trainingSnapshot.docs[0].id,
          title: 'Training Program',
          route: '/academy/dashboard'
        });
      }

      // Check 3: General investors
      const investorDoc = doc(firestore, "Investors", userId);
      const investorSnap = await getDoc(investorDoc);
      
      if (investorSnap.exists()) {
        const userData = investorSnap.data();
        foundAccounts.push({
          type: 'general',
          data: userData,
          id: investorSnap.id,
          title: 'General Investment',
          route: '/userdash'
        });
      }

      return foundAccounts;
    } catch (error) {
      console.error("Error finding user accounts:", error);
      return [];
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Skip collection checking for /agreement page
        if (window.location.pathname === '/agreement') { 
          setUser(user);
          return;
        }

        // Find user in all collections
        const accounts = await findUserInAllCollections(user.email, user.uid);
        
        if (accounts.length > 0) {
          setUser(user);
          setUserAccounts(accounts);
          setHasMultipleAccounts(accounts.length > 1);
          
          // Store accounts info for dashboard selection
          localStorage.setItem('multipleAccounts', JSON.stringify(accounts));
          localStorage.setItem('userEmail', user.email);
        } 
      } else {
        setUser(null);
        setUserAccounts([]);
        setHasMultipleAccounts(false);
        localStorage.clear();
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // Handle dashboard selection
  const handleDashboardSelection = () => {
    if (hasMultipleAccounts) {
      navigate('/dashboard-selection');
    } else if (userAccounts.length === 1) {
      // Single account - direct navigation
      const account = userAccounts[0];
      localStorage.setItem('userType', account.type);
      localStorage.setItem('userId', account.data.userId || account.id);
      
      if (account.type === 'investment' && account.route === '/USDTPayment') {
        navigate('/USDTPayment', { 
          state: { 
            plan: account.data.subscriptionPlan,
            userId: account.data.userId,
            registrationId: account.id
          }
        });
      } else {
        navigate(account.route);
      }
    }
    closeMenu();
  };

  // Create dynamic menu items
  const getMenuItems = () => {
    const baseItems = [
      { to: '/', label: 'Home', icon: '🏠' },
      { to: '/algo-trading-academy', label: 'Algo Trading Academy', icon: '🎓' },
      { to: '/tools/protrader-suite', label: 'ProTrader Suite', icon: '✨' },
      { to: '/investment-funds', label: 'Institutional Funds', icon: '🏢' },
      { to: '/services', label: 'All Services', icon: '⚡' },
      { to: '/trading-tools', label: 'Trading Tools', icon: '🔧' },
      { to: '/heatmaps', label: 'Market Heatmaps', icon: '🔥' },
      { to: '/free-tools', label: 'Free Tools', icon: '🎁' },
      { to: '/vps', label: 'ProTrader VPS', icon: '🖥️' },
      { to: '/blog', label: 'Blog', icon: '📰' },
      { to: '/about-us', label: 'About Us', icon: '👥' },
      { to: '/contact-us', label: 'Contact', icon: '📧' },
      { href: 'https://www.mql5.com/en/users/adiec7/seller', label: 'MQL5 Systems', icon: '🤖', external: true },
    ];

    if (user) {
      // Add dashboard options for logged in users
      if (hasMultipleAccounts) {
        baseItems.push({ action: handleDashboardSelection, label: 'My Dashboards', icon: '📊' });
      } else if (userAccounts.length === 1) {
        baseItems.push({ action: handleDashboardSelection, label: userAccounts[0].title, icon: '📊' });
      } else {
        baseItems.push({ to: '/userdash', label: 'Dashboard', icon: '📊' });
      }
    } else {
      baseItems.push({ to: '/login', label: 'Sign In | Up', icon: '🔐' });
    }

    return baseItems;
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const closeMenu = () => {
    setIsOpen(false);
  };

  return (
    <>
      <nav className="navbar">
        <Link to="/" className="navbar-brand">
          <span className="brand-text">KS</span>
          <span className="brand-accent">Quantitative</span>
        </Link>

        {/* Modern Hamburger Button */}
        <button 
          className={`hamburger-btn ${isOpen ? 'active' : ''}`}
          onClick={toggleMenu}
          aria-label="Toggle menu"
        >
          <div className="hamburger-icon">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </button>

        {/* Modern Side Menu */}
        <div className={`side-menu ${isOpen ? 'open' : ''}`}>
          <div className="menu-overlay" onClick={closeMenu}></div>
          
          <div className="menu-content">
            {/* Header */}
            <div className="menu-header">
              <div className="menu-logo">
                <span className="menu-brand-text">KS</span>
                <span className="menu-brand-accent">Quantitative</span>
              </div>
              <button className="close-btn" onClick={closeMenu}>
                <FaTimes />
              </button>
            </div>

            {/* User Welcome */}
            {user && (
              <div className="user-welcome">
                <div className="user-avatar">
                  {user.displayName?.charAt(0) || user.email?.charAt(0) || 'U'}
                </div>
                <div className="user-info">
                  <span className="user-name">
                    Welcome, {user.displayName?.split(' ')[0] || user.email?.split('@')[0]}!
                  </span>
                  {hasMultipleAccounts && (
                    <span className="account-count">
                      {userAccounts.length} accounts available
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Navigation Links */}
            <div className="menu-items">
              {getMenuItems().map((item, index) => (
                item.action ? (
                  <button
                    key={index}
                    onClick={item.action}
                    className="menu-item"
                  >
                    <span className="menu-icon">{item.icon}</span>
                    <span className="menu-label">{item.label}</span>
                    <span className="menu-arrow">→</span>
                  </button>
                ) : item.external ? (
                  <a
                    key={item.href}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="menu-item"
                    onClick={closeMenu}
                  >
                    <span className="menu-icon">{item.icon}</span>
                    <span className="menu-label">{item.label}</span>
                    <span className="menu-arrow">↗</span>
                  </a>
                ) : (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="menu-item"
                    onClick={closeMenu}
                  >
                    <span className="menu-icon">{item.icon}</span>
                    <span className="menu-label">{item.label}</span>
                    <span className="menu-arrow">→</span>
                  </Link>
                )
              ))}
            </div>

            {/* Social Media Section */}
            <div className="menu-footer">
              <div className="social-section">
                <h4 className="social-title">Connect With Us</h4>
                <div className="social-icons">
                  <a 
                    href="https://www.linkedin.com/company/ksmarket/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="social-icon linkedin"
                  >
                    <FaLinkedin />
                  </a>
                  <a 
                    href="https://www.instagram.com/ksquants" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="social-icon instagram"
                  >
                    <FaInstagram />
                  </a>
                  <a 
                    href="#https://www.facebook.com" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="social-icon twitter"
                  >
                    <FaTwitter />
                  </a>
                </div>
              </div>
              
              {/* Sign Out */}
              {user && (
                <div className="signout-section">
                  <SignOut />
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;